/**
 * Agenda · gestão de clínica (camada transversal — ADR-22 / docs/arquitetura/09).
 *
 * Modela-se MULTI desde o início (clínica com N profissionais; paciente que
 * consulta com >1 profissional), mas a experiência é SOLO: na 1ª ativação cria-se
 * uma "clínica de 1" automaticamente. Assim não há migração dolorosa quando crescer.
 *
 * Fase 1 = SÓ O CALENDÁRIO, provedor `nativo`. Sem omnichannel/Synex (Fase 2) e
 * sem Google Calendar (backlog). Nada de dado clínico aqui — a `observacao_agenda`
 * é administrativa (ex.: "trazer exames"), visível à secretária; prontuário fica fora.
 *
 * Tudo local-first (Dexie). Enquanto `config.agenda_ativa === false`, nada disto é
 * tocado e a Agenda não aparece — é a camada "contratável".
 */
import type { EspecialidadeProf, ProvedorAgenda } from "../types";

export type { ProvedorAgenda };

// ── Escopo de clínica (paralelo ao anel de cuidado por paciente) ─────────────

/** RBAC de clínica (distinto do vínculo de cuidado, que é por paciente). */
export type PapelClinica = "admin" | "profissional" | "secretaria";

export const PAPEL_LABEL: Record<PapelClinica, string> = {
  admin: "administração",
  profissional: "profissional",
  secretaria: "secretaria",
};

/** A clínica. Solo = "clínica de 1" criada na 1ª ativação. */
export interface Clinica {
  id: string;
  nome: string;
  dono_nome: string | null; // o profissional dono (solo) — usado na saudação/UI
  solo: boolean; // true enquanto for clínica de 1 (esconde a complexidade de "clínica")
  criado_em: string; // ISO 8601
}

/**
 * Membro da clínica. Na experiência solo há 1 (o dono, admin+profissional).
 * Adicionar um colega/secretária é o que "revela" a clínica multi.
 */
export interface MembroClinica {
  id: string;
  clinica_id: string;
  nome: string;
  papel: PapelClinica;
  especialidade: EspecialidadeProf | null; // só p/ 'profissional'
  registro_classe: string | null; // CRFa / CREFITO / CRP / CRN
  cor: string | null; // cor no calendário (deriva da especialidade por padrão)
  arquivado: boolean;
  criado_em: string;
}

/** Consultório (opcional). */
export interface LocalClinica {
  id: string;
  clinica_id: string;
  nome: string;
  endereco: string | null;
  arquivado: boolean;
  criado_em: string;
}

/**
 * Janela de disponibilidade recorrente do profissional (provedor `nativo`).
 * Só faz sentido no nativo; no Google a disponibilidade é o próprio calendário dele.
 */
export interface Disponibilidade {
  id: string;
  clinica_id: string;
  profissional_id: string; // MembroClinica.id
  dia_semana: number; // 0=domingo … 6=sábado
  hora_inicio: string; // "HH:MM"
  hora_fim: string; // "HH:MM"
  criado_em: string;
}

// ── Agendamento (o evento que cruza clínica × paciente) ──────────────────────

export type TipoConsulta = "avaliacao" | "retorno" | "sessao" | "teleconsulta";

export const TIPO_LABEL: Record<TipoConsulta, string> = {
  avaliacao: "avaliação",
  retorno: "retorno",
  sessao: "sessão",
  teleconsulta: "teleconsulta",
};

export type Modalidade = "presencial" | "online";

export const MODALIDADE_LABEL: Record<Modalidade, string> = {
  presencial: "presencial",
  online: "online",
};

/** Ciclo de vida do status (docs/arquitetura/09 §4). Nunca usa vermelho na UI. */
export type StatusAgendamento =
  | "agendado"
  | "confirmado"
  | "realizado"
  | "faltou"
  | "remarcado"
  | "cancelado";

export const STATUS_AGENDA_ORDEM: StatusAgendamento[] = [
  "agendado",
  "confirmado",
  "realizado",
  "faltou",
  "remarcado",
  "cancelado",
];

/** glifo (forma) + rótulo + classe — codificar por forma, não só cor (princípio 3). */
export const STATUS_AGENDA_META: Record<
  StatusAgendamento,
  { glifo: string; rotulo: string; classe: string }
> = {
  agendado: { glifo: "○", rotulo: "agendado", classe: "ag-st-agendado" },
  confirmado: { glifo: "◐", rotulo: "confirmado", classe: "ag-st-confirmado" },
  realizado: { glifo: "●", rotulo: "realizado", classe: "ag-st-realizado" },
  faltou: { glifo: "◇", rotulo: "faltou", classe: "ag-st-faltou" },
  remarcado: { glifo: "◑", rotulo: "remarcado", classe: "ag-st-remarcado" },
  cancelado: { glifo: "×", rotulo: "cancelado", classe: "ag-st-cancelado" },
};

/**
 * O agendamento. Cruza a clínica (profissional) e o anel de cuidado (paciente).
 * `paciente_id` liga a uma `crianca` cadastrada (a mesma dos módulos) quando existe;
 * `paciente_nome` é o nome de exibição (sempre presente) e cobre pacientes do
 * profissional que ainda não estão cadastrados neste aparelho.
 */
export interface Agendamento {
  id: string;
  clinica_id: string;
  profissional_id: string; // MembroClinica.id
  paciente_id: string | null; // crianca.id (opcional)
  paciente_nome: string; // denormalizado p/ exibição (a secretária vê só nome+contato)
  paciente_contato: string | null; // telefone/whatsapp p/ confirmar (Fase 2)
  data: string; // "YYYY-MM-DD" (chave do dia no calendário)
  hora_inicio: string; // "HH:MM"
  duracao_min: number;
  tipo: TipoConsulta;
  modalidade: Modalidade;
  local_id: string | null;
  link_online: string | null;
  status: StatusAgendamento;
  observacao_agenda: string | null; // ADMINISTRATIVA — nunca prontuário
  provedor: ProvedorAgenda; // "nativo" na Fase 1
  criado_em: string; // ISO 8601
  atualizado_em: string; // ISO 8601
}
